import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface AddResourceModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: {
    resource_arn: string;
    resource_type: string;
    resource_name: string;
    region: string;
  }) => Promise<void>;
}

const RESOURCE_TYPES = [
  { value: "lambda", label: "Lambda" },
  { value: "s3", label: "S3" },
  { value: "rds", label: "RDS" },
  { value: "ecs", label: "ECS / Fargate" },
  { value: "apigateway", label: "API Gateway" },
  { value: "dynamodb", label: "DynamoDB" },
  { value: "sqs", label: "SQS" },
  { value: "cloudfront", label: "CloudFront" },
  { value: "ec2", label: "EC2" },
  { value: "elasticache", label: "ElastiCache" },
  { value: "sns", label: "SNS" },
];

const REGIONS = [
  "eu-west-1",
  "eu-west-2",
  "eu-central-1",
  "us-east-1",
  "us-west-2",
  "ap-southeast-1",
];

export function AddResourceModal({ open, onClose, onAdd }: AddResourceModalProps) {
  const [arn, setArn] = useState("");
  const [resourceType, setResourceType] = useState("lambda");
  const [resourceName, setResourceName] = useState("");
  const [region, setRegion] = useState("eu-west-1");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!arn.trim() || !resourceName.trim()) return;
    setSubmitting(true);
    try {
      await onAdd({
        resource_arn: arn.trim(),
        resource_type: resourceType,
        resource_name: resourceName.trim(),
        region,
      });
      setArn("");
      setResourceName("");
      setResourceType("lambda");
      setRegion("eu-west-1");
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Cloud Resource</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Resource ARN</Label>
            <Input
              value={arn}
              onChange={(e) => setArn(e.target.value)}
              placeholder="arn:aws:lambda:eu-west-1:123456789:function:my-function"
            />
          </div>
          <div className="space-y-2">
            <Label>Resource Name</Label>
            <Input
              value={resourceName}
              onChange={(e) => setResourceName(e.target.value)}
              placeholder="my-function"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={resourceType} onValueChange={setResourceType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESOURCE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Region</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REGIONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!arn.trim() || !resourceName.trim() || submitting}>
            {submitting ? "Adding..." : "Add Resource"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
